import React from 'react';
import { CommonEventOptions, TrackPageViewArguments } from '@westwing/appshell_types';
import { useIdsGaTracking } from '.';

type TrackingData = TrackPageViewArguments | CommonEventOptions;

/**
 * Hook for implementing "one-off" tracking events. Only the first event is tracked.
 */
export default function useOneOffTracking(trackingData: TrackingData) {
    const { trackPageView, trackIdsEvent } = useIdsGaTracking();
    const [trackingState, setTrackingState] = React.useState({
        pageviewTriggered: false,
        eventTriggered: false,
    });

    const trackPageViewEvent = React.useCallback(async () => {
        const alreadyTracked = trackingState.pageviewTriggered;
        if (!alreadyTracked) {
            trackPageView(trackingData as TrackPageViewArguments);
            setTrackingState(prev => ({ ...prev, pageviewTriggered: true }));
        }
    }, [trackingState.pageviewTriggered, trackPageView, trackingData]);

    const trackEvent = React.useCallback(async () => {
        const alreadyTracked = trackingState.eventTriggered;
        if (!alreadyTracked) {
            trackIdsEvent(trackingData as CommonEventOptions);
            setTrackingState(prev => ({ ...prev, eventTriggered: true }));
        }
    }, [trackingState.eventTriggered, trackIdsEvent, trackingData]);

    const resetPageViewCheck = React.useCallback(() => {
        setTrackingState(prev => ({ ...prev, pageviewTriggered: false }));
    }, []);

    const resetCheck = React.useCallback(() => {
        setTrackingState(prev => ({ ...prev, eventTriggered: false }));
    }, []);

    return { resetPageViewCheck, trackPageViewEvent, resetCheck, trackEvent };
}
